import { useState, useEffect, useCallback } from "react";
import { BookOpen, ClipboardList, TrendingUp, Target, Layers, Award, ArrowRight, PlayCircle, Calendar, MessageCircle, Trophy, Sparkles } from "lucide-react";
import axios from "axios";
import { t } from "@/lib/i18n";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from "recharts";

// Manus-spec soft palette: corporate blues, mint greens, soft slates
const COLORS = ['#2563EB', '#3B82F6', '#60A5FA', '#10B981', '#34D399', '#8B5CF6', '#A78BFA', '#94A3B8', '#CBD5E1'];
const PIE_COLORS = ['#2563EB', '#10B981', '#8B5CF6', '#F59E0B'];

export const AnalyticsDashboard = ({ apiUrl, readProgress, organSections, onSectionChange, lang, studentName, organ1Unlocked = true }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${apiUrl}/stats`);
      setStats(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [apiUrl]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-3 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!stats) return null;

  // Continue Learning
  const lastLocation = (() => {
    try {
      return JSON.parse(localStorage.getItem('bss.lastLocation') || 'null');
    } catch { return null; }
  })();

  const lastOrgan = lastLocation ? organSections?.find(o => o.id === lastLocation.organId) : null;
  const lastOrganIdx = lastOrgan ? organSections.indexOf(lastOrgan) : -1;

  // Prepare chart data
  const organBarData = stats.organ_stats.map((o, i) => ({
    name: `Organ ${i + 1}`,
    "Sub-Topik": o.topics,
    "Point": o.points,
  }));

  const scoreDistData = Object.entries(stats.score_distribution)
    .map(([name, value]) => ({ name, value }))
    .filter(d => d.value > 0);

  const hasQuizData = stats.total_attempts > 0;

  const recentData = stats.recent_attempts.map((a, i) => ({
    name: a.quiz_title.length > 15 ? a.quiz_title.slice(0, 15) + '...' : a.quiz_title,
    Skor: a.score,
  }));

  // Donut for content coverage
  const contentData = stats.organ_stats.map((o, i) => ({
    name: `Organ ${i + 1}`,
    value: o.topics + o.points,
  }));

  const organPercents = (readProgress && typeof readProgress === 'object')
    ? Object.values(readProgress).map(p => Number(p?.percent || 0))
    : [];
  const overallProgress = organPercents.length
    ? Math.round(organPercents.reduce((a, b) => a + b, 0) / organPercents.length)
    : 0;
  const completedOrgans = organPercents.filter(p => p >= 100).length;
  const today = new Date().toLocaleDateString(lang === 'en' ? 'en-MY' : 'ms-MY', { weekday: 'long', day: 'numeric', month: 'long' });
  const studentLabel = studentName || 'Student';
  const greetingTitle = lang === 'en' ? 'Hi, ' + studentLabel : 'Hai, ' + studentLabel;
  let greetingSubtitle;
  if (overallProgress === 0 && !organ1Unlocked) {
    greetingSubtitle = lang === 'en'
      ? 'Register for a session and scan the attendance QR to unlock Organ 1.'
      : 'Daftar Sesi & Event dan imbas QR kehadiran untuk membuka Organ 1.';
  } else if (overallProgress === 0) {
    greetingSubtitle = lang === 'en'
      ? 'Ready to start your transformation? Open Organ 1 to begin.'
      : 'Bersedia untuk mulakan transformasi anda? Buka Organ 1 untuk mula.';
  } else if (overallProgress >= 100) {
    greetingSubtitle = lang === 'en'
      ? 'Incredible! You have mastered all ' + completedOrgans + ' organs.'
      : 'Hebat! Anda telah kuasai semua ' + completedOrgans + ' organ.';
  } else {
    const suffix = completedOrgans === 1 ? '' : 's';
    greetingSubtitle = lang === 'en'
      ? 'You have completed ' + completedOrgans + ' organ' + suffix + ' so far - keep going!'
      : 'Anda telah lengkap ' + completedOrgans + ' organ setakat ini - teruskan!';
  }

  return (
    <div className="space-y-5">
      {/* Greeting Card - light/white with subtle gradient */}
      <div className="rounded-[20px] p-6 sm:p-8 relative overflow-hidden bg-white border border-[#F1F5F9] shadow-sm">
        <div className="absolute top-0 right-0 w-64 h-64 rounded-full opacity-[0.08]" style={{ background: 'radial-gradient(circle, #2563EB 0%, transparent 70%)', transform: 'translate(30%, -30%)' }} />
        <div className="relative z-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
          {/* Left: greeting */}
          <div className="flex-1">
            <p className="text-[#2563EB] text-[11px] font-bold uppercase tracking-[0.18em] mb-2">
              {today}
            </p>
            <h2 className="text-4xl sm:text-5xl font-extrabold text-[#0F172A] mb-2 leading-[1.05]" style={{ fontFamily: 'Inter, sans-serif', letterSpacing: '-0.04em' }}>
              {greetingTitle}
            </h2>
            <p className="text-[#64748B] text-sm max-w-md">
              {greetingSubtitle}
            </p>
          </div>

          {/* Right: progress widget */}
          <div className="flex items-center gap-5 sm:gap-6">
            <div className="text-right">
              <p className="text-[#94A3B8] text-[10px] font-semibold uppercase tracking-widest mb-1">
                {lang === 'en' ? 'Overall progress' : 'Kemajuan keseluruhan'}
              </p>
              <p className="text-[#0F172A] text-2xl sm:text-3xl font-bold leading-tight">{overallProgress}%</p>
              <p className="text-[#94A3B8] text-xs">
                {completedOrgans} / {stats.total_organs || 5} {lang === 'en' ? 'organs' : 'organ'}
              </p>
            </div>
            {/* Circular progress ring */}
            <div
              className="relative w-20 h-20 sm:w-24 sm:h-24 flex-shrink-0 rounded-full"
              style={{
                background: `conic-gradient(#2563EB ${overallProgress * 3.6}deg, #E2E8F0 0deg)`,
              }}
            >
              <div className="absolute inset-[6px] rounded-full bg-white flex items-center justify-center">
                <PlayCircle size={22} className="text-[#2563EB]" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* First-Action Prompt - shows for new users with 0% progress */}
      {readProgress && Object.values(readProgress).every(p => !p || p.percent === 0) && !hasQuizData && (
        <div
          onClick={() => organSections?.length > 0 && onSectionChange(organSections[0].id)}
          className="rounded-[20px] p-5 bg-white border border-[rgba(0,0,0,0.08)] cursor-pointer hover:border-[#2563EB] hover:shadow-[0_8px_24px_rgba(37,99,235,0.08)] transition-all duration-200 group"
          data-testid="first-action-prompt"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-[14px] bg-[#EFF6FF] flex items-center justify-center flex-shrink-0 group-hover:bg-[#2563EB] transition-colors duration-200">
              <BookOpen size={22} className="text-[#2563EB] group-hover:text-white transition-colors" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-bold text-[#0A0A0A]">{lang === 'en' ? 'Your First Step' : 'Langkah Pertama Anda'}</p>
              <p className="text-xs text-[rgba(0,0,0,0.5)] mt-0.5">{lang === 'en' ? 'Start by reading Organ 1 - Leading The New Normal. Tap here to begin!' : 'Mula dengan membaca Organ 1 - Leading The New Normal. Klik di sini untuk mula!'}</p>
            </div>
            <ArrowRight size={18} className="text-[#2563EB] flex-shrink-0 group-hover:translate-x-1 transition-transform" />
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
        <button onClick={() => organSections?.length > 0 && onSectionChange(organSections[0].id)} className="flex items-center gap-3 p-3.5 rounded-[16px] bg-white border border-[#E2E8F0] hover:shadow-md hover:border-[#2563EB] hover:translate-y-[-2px] transition-all duration-200 group" data-testid="quick-action-read">
          <div className="w-9 h-9 rounded-[10px] bg-[#EFF6FF] flex items-center justify-center flex-shrink-0"><BookOpen size={16} className="text-[#2563EB]" /></div>
          <div className="text-left"><p className="text-xs font-bold text-[#0F172A]">{lang === 'en' ? 'Start Reading' : 'Mula Baca'}</p><p className="text-[10px] text-[#64748B]">Organ 1</p></div>
        </button>
        <button onClick={() => onSectionChange('events')} className="flex items-center gap-3 p-3.5 rounded-[16px] bg-white border border-[#E2E8F0] hover:shadow-md hover:border-[#2563EB] hover:translate-y-[-2px] transition-all duration-200 group" data-testid="quick-action-events">
          <div className="w-9 h-9 rounded-[10px] bg-emerald-50 flex items-center justify-center flex-shrink-0"><Calendar size={16} className="text-emerald-600" /></div>
          <div className="text-left"><p className="text-xs font-bold text-[#0F172A]">{lang === 'en' ? 'Sessions' : 'Sesi & Event'}</p><p className="text-[10px] text-[#64748B]">{lang === 'en' ? 'Upcoming' : 'Akan Datang'}</p></div>
        </button>
        <button onClick={() => onSectionChange('community')} className="flex items-center gap-3 p-3.5 rounded-[16px] bg-white border border-[#E2E8F0] hover:shadow-md hover:border-[#2563EB] hover:translate-y-[-2px] transition-all duration-200 group" data-testid="quick-action-community">
          <div className="w-9 h-9 rounded-[10px] bg-[#EFF6FF] flex items-center justify-center flex-shrink-0"><MessageCircle size={16} className="text-[#2563EB]" /></div>
          <div className="text-left"><p className="text-xs font-bold text-[#0F172A]">{lang === 'en' ? 'Community' : 'Komuniti'}</p><p className="text-[10px] text-[#64748B]">{lang === 'en' ? 'Discuss' : 'Berbincang'}</p></div>
        </button>
        <button onClick={() => onSectionChange('leaderboard')} className="flex items-center gap-3 p-3.5 rounded-[16px] bg-white border border-[#E2E8F0] hover:shadow-md hover:border-[#2563EB] hover:translate-y-[-2px] transition-all duration-200 group" data-testid="quick-action-leaderboard">
          <div className="w-9 h-9 rounded-[10px] bg-emerald-50 flex items-center justify-center flex-shrink-0"><Trophy size={16} className="text-emerald-600" /></div>
          <div className="text-left"><p className="text-xs font-bold text-[#0F172A]">Leaderboard</p><p className="text-[10px] text-[#64748B]">{lang === 'en' ? 'Your Rank' : 'Kedudukan'}</p></div>
        </button>
      </div>

      {/* Continue Learning Card */}
      {lastOrgan && (
        <div
          className="dash-card border-l-4 border-l-[#2563EB] cursor-pointer hover:shadow-md transition-all duration-200"
          onClick={() => onSectionChange && onSectionChange(lastOrgan.id)}
          data-testid="continue-learning-card"
        >
          <div className="p-4 flex items-center gap-4">
            <div className="w-11 h-11 rounded-xl bg-[#F7F7F7] flex items-center justify-center flex-shrink-0">
              <PlayCircle size={22} className="text-[#0A0A0A]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-semibold text-[#2563EB] uppercase tracking-wide">{t('continueLearning', lang)}</p>
              <p className="text-sm font-bold text-[#0A0A0A] truncate">
                Organ {lastOrganIdx + 1} - {lastOrgan.title.replace(/ORGAN \d+\s*–\s*/, '')}
              </p>
            </div>
            <ArrowRight size={18} className="text-[#2563EB] flex-shrink-0" />
          </div>
        </div>
      )}

      {/* Stat Cards - Unified black + blue theme */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <StatCard icon={<Layers size={20} />} value={stats.total_organs} label={t('organ', lang)} />
        <StatCard icon={<BookOpen size={20} />} value={stats.total_topics} label={t('subTopik', lang)} />
        <StatCard icon={<Target size={20} />} value={stats.total_points} label="Point" />
        <StatCard icon={<ClipboardList size={20} />} value={stats.total_quizzes} label="Quiz" />
        <StatCard icon={<Award size={20} />} value={stats.total_attempts > 0 ? `${stats.avg_score}%` : '-'} label={lang === 'en' ? 'Platform Avg' : 'Purata Platform'} />
      </div>

      {/* Reading Progress Per Organ */}
      <div className="dash-card">
        <div className="dash-card-header">
          <h3><BookOpen size={16} className="text-[#0A0A0A]" /> {t('readingProgress', lang)}</h3>
        </div>
        <div className="p-4 space-y-3">
          {organSections?.map((organ, idx) => {
            const prog = readProgress ? readProgress[organ.id] : null;
            const pct = prog ? prog.percent : 0;
            return (
              <div key={organ.id} className="flex items-center gap-3 cursor-pointer hover:bg-[#F7F7F7] dark:hover:bg-gray-800 -mx-2 px-2 py-1 rounded-lg transition-colors" onClick={() => onSectionChange(organ.id)}>
                <span className="text-xs font-semibold text-[rgba(0,0,0,0.55)] w-16 flex-shrink-0">Organ {idx + 1}</span>
                <div className="flex-1 h-2.5 bg-[#F2F2F2] dark:bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{ width: `${Math.max(pct, 2)}%`, background: pct === 100 ? '#10B981' : pct > 0 ? '#2563EB' : '#E5E7EB' }}
                  />
                </div>
                <span className="text-xs font-mono text-[rgba(0,0,0,0.45)] w-10 text-right">{pct}%</span>
              </div>
            );
          })}
          {/* Motivational message for 0% progress */}
          {readProgress && Object.values(readProgress).every(p => !p || p.percent === 0) && (
            <div className="text-center pt-2 pb-1">
              <p className="text-xs text-[rgba(0,0,0,0.35)] italic">{lang === 'en' ? 'Start reading any Organ to track your progress here!' : 'Mula baca mana-mana Organ untuk lihat kemajuan anda di sini!'}</p>
            </div>
          )}
        </div>
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Bar Chart: Sub-topik & Points per Organ */}
        <div className="dash-card">
          <div className="dash-card-header">
            <h3><Layers size={16} className="text-[#0A0A0A]" /> Sub-Topik & Point per Organ</h3>
          </div>
          <div className="p-4" style={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={organBarData} barGap={4}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#9ca3af' }} />
                <YAxis tick={{ fontSize: 12, fill: '#9ca3af' }} />
                <Tooltip
                  contentStyle={{ borderRadius: '0.5rem', border: '1px solid #eee', fontSize: '0.8rem' }}
                />
                <Bar dataKey="Sub-Topik" fill="#2563EB" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Point" fill="#0A0A0A" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Donut: Content Distribution per Organ */}
        <div className="dash-card">
          <div className="dash-card-header">
            <h3><Target size={16} className="text-[#0A0A0A]" /> Agihan Kandungan</h3>
          </div>
          <div className="p-4" style={{ height: 320 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart margin={{ top: 20, right: 30, bottom: 20, left: 30 }}>
                <Pie
                  data={contentData}
                  cx="50%"
                  cy="45%"
                  innerRadius={55}
                  outerRadius={85}
                  paddingAngle={3}
                  dataKey="value"
                  label={({ name, value }) => `${name}: ${value}`}
                  labelLine={false}
                >
                  {contentData.map((entry, index) => (
                    <Cell key={entry.name} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: '0.5rem', border: '1px solid #eee', fontSize: '0.8rem' }} />
                <Legend wrapperStyle={{ fontSize: '0.75rem', paddingTop: '8px' }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Charts Row 2: Quiz Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Pie: Score Distribution */}
        <div className="dash-card">
          <div className="dash-card-header">
            <h3><Award size={16} className="text-[#0A0A0A]" /> Agihan Skor Quiz</h3>
          </div>
          <div className="p-4" style={{ height: 300 }}>
            {hasQuizData ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={scoreDistData}
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    dataKey="value"
                    label={({ name, value }) => `${name}: ${value}`}
                    labelLine={true}
                  >
                    {scoreDistData.map((entry, index) => (
                      <Cell key={entry.name} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ borderRadius: '0.5rem', border: '1px solid #eee', fontSize: '0.8rem' }} />
                  <Legend wrapperStyle={{ fontSize: '0.75rem' }} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <EmptyChart message="Tiada data quiz lagi" sub="Cuba quiz untuk lihat agihan skor." icon="quiz" />
            )}
          </div>
        </div>

        {/* Bar: Recent Quiz Scores */}
        <div className="dash-card">
          <div className="dash-card-header">
            <h3><TrendingUp size={16} className="text-[#0A0A0A]" /> Skor Quiz Terkini</h3>
          </div>
          <div className="p-4" style={{ height: 300 }}>
            {hasQuizData && recentData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={recentData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#9ca3af' }} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 12, fill: '#9ca3af' }} />
                  <Tooltip contentStyle={{ borderRadius: '0.5rem', border: '1px solid #eee', fontSize: '0.8rem' }} />
                  <Bar dataKey="Skor" radius={[6, 6, 0, 0]}>
                    {recentData.map((entry, index) => (
                      <Cell key={index} fill={entry.Skor >= 70 ? '#10B981' : entry.Skor >= 40 ? '#2563EB' : '#94A3B8'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <EmptyChart message="Tiada percubaan quiz lagi" sub="Cuba quiz untuk lihat skor." icon="chart" />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

function StatCard({ icon, value, label }) {
  return (
    <div className="stat-card" data-testid={`stat-card-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="w-9 h-9 rounded-xl bg-[#EFF6FF] flex items-center justify-center mb-2">
        <span className="text-[#2563EB]">{icon}</span>
      </div>
      <div className="stat-card-value">{value}</div>
      <div className="stat-card-label">{label}</div>
    </div>
  );
}

function EmptyChart({ message, sub, icon }) {
  return (
    <div className="h-full flex items-center justify-center">
      <div className="text-center">
        <div className="w-16 h-16 rounded-2xl bg-gray-50 flex items-center justify-center mx-auto mb-3">
          {icon === 'quiz' ? (
            <ClipboardList size={28} className="text-gray-200" />
          ) : (
            <TrendingUp size={28} className="text-gray-200" />
          )}
        </div>
        <p className="text-sm font-medium text-gray-400">{message}</p>
        {sub && <p className="text-xs text-gray-300 mt-1">{sub}</p>}
      </div>
    </div>
  );
}
